import { Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CoreButton } from "@verity/components/core";
import { useContext } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../../../helpers/api-client";
import { intIdToUuid } from "../../../../../helpers/uuid";
import i18n from "../../../../../i18n";
import { PortalControllerContext } from "../../../../context";
import { CalendarMissedIcon } from "../assets";
import DataBar from "../components/DataBar";
import InfoCard from "../components/InfoCard";
import { CandidateOptionsType, SessionType } from "../types";
import styles from "./InterviewUnattendedPage.module.css";
import UnattendedPageSkeleton from "./UnattendedPageSkeleton";

type InterviewUnattendedPageProps = {
	assessmentId: number;
	candidateOptions: CandidateOptionsType;
};

export default function InterviewUnattendedPage({ assessmentId, candidateOptions }: InterviewUnattendedPageProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { instituteUuid, courseUuid } = portalContext;

	const navigate = useNavigate();

	const {
		data: session,
		error,
		isLoading,
	} = useQuery({
		queryKey: ["user-live-session", instituteUuid, courseUuid, assessmentId],
		enabled: !!instituteUuid && !!courseUuid && !!assessmentId,
		queryFn: async (): Promise<SessionType> => {
			const result = await apiClient.liveClient.getLiveSessionsWithUsers({
				instituteUuid: instituteUuid!,
				courseUuid: courseUuid!,
				assessmentUuid: intIdToUuid(assessmentId),
			});

			if (!result.results[0]) {
				throw new Error("Missing live session");
			}

			return result.results[0];
		},
	});

	if (isLoading) return <UnattendedPageSkeleton />;

	if (error) console.error(error);

	return (
		<div className={styles.wrapper}>
			<Stack>
				<Typography className={styles.name}>{candidateOptions.candidateName}</Typography>
				<Typography className={styles.position}>{candidateOptions.positionName}</Typography>
			</Stack>

			<InfoCard>
				<Trans
					i18nKey="candidate.resultsPage.unattended.message"
					components={{
						bold: <span className={styles.bold} />,
					}}
				/>
			</InfoCard>

			<DataBar
				candidateName={candidateOptions.candidateName}
				positionName={candidateOptions.positionName}
				startTime={session?.startTime ? new Date(session.startTime) : undefined}
			/>

			<div className={styles.nextSteps}>
				<div className={styles.iconWrapper}>
					<CalendarMissedIcon />
				</div>
				<Typography className={styles.title}>{i18n.t("candidate.resultsPage.unattended.nextSteps.title")}</Typography>
				<Typography className={styles.description}>
					{i18n.t("candidate.resultsPage.unattended.nextSteps.description")}
				</Typography>
				<CoreButton onClick={() => navigate("/interviews/schedule")}>
					{i18n.t("candidate.resultsPage.unattended.button.reschedule")}
				</CoreButton>
			</div>
		</div>
	);
}
